import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  groupFilterIndexes,
  isFilterComplete,
  normalizeFilterConjunction
} from '../../utils/customFieldUtils';
import { datePickerTriggers, DATE_INPUT_MIN, DATE_INPUT_MAX } from '../../utils/dateUtils';
import './CustomFilters.css';

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does Not Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does Not Contain' },
  { value: 'has_value', label: 'Has a Value' },
  { value: 'does_not_have_value', label: 'Does Not Have a Value' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' }
];

let filterIdCounter = 0;
const nextFilterId = () => `cf-${Date.now().toString(36)}-${(filterIdCounter += 1)}`;

/**
 * Give every row a unique id and a normalized conjunction. The first row's
 * conjunction is never used (nothing precedes it), so it is pinned to 'AND'.
 */
const normalizeFilterList = (list) => {
  const seenIds = new Set();

  return (Array.isArray(list) ? list : []).map((filter, index) => {
    let id = filter?.id;
    if (id === undefined || id === null || id === '' || seenIds.has(id)) {
      id = nextFilterId();
    }
    seenIds.add(id);

    return {
      ...filter,
      id,
      conjunction: index === 0 ? 'AND' : normalizeFilterConjunction(filter?.conjunction)
    };
  });
};

const shouldShowValueInput = (operator) => !['has_value', 'does_not_have_value'].includes(operator);

function ConjunctionToggle({ value, onChange, disabled = false }) {
  const current = normalizeFilterConjunction(value);

  return (
    <div className={`filter-conjunction ${current === 'OR' ? 'is-or' : 'is-and'}`}>
      <span className="filter-conjunction-line" aria-hidden="true"></span>
      <div className="filter-conjunction-toggle" role="group" aria-label="Join with previous filter">
        {['AND', 'OR'].map((option) => (
          <button
            key={option}
            type="button"
            className={`filter-conjunction-option ${current === option ? 'active' : ''}`}
            aria-pressed={current === option}
            disabled={disabled}
            onClick={() => onChange(option)}
          >
            {option}
          </button>
        ))}
      </div>
      <span className="filter-conjunction-line" aria-hidden="true"></span>
    </div>
  );
}

function SortableFilterRow({
  id,
  index,
  total,
  incomplete,
  onRemove,
  onMoveUp,
  onMoveDown,
  children
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`custom-filter-row ${isDragging ? 'dragging' : ''} ${incomplete ? 'incomplete' : ''}`}
    >
      <div className="filter-row-header">
        <div className="filter-row-heading">
          <button
            type="button"
            className="filter-drag-handle"
            aria-label={`Reorder filter ${index + 1}`}
            {...attributes}
            {...listeners}
          >
            <i className="bi bi-grip-vertical"></i>
          </button>
          <span className="filter-index">Filter {index + 1}</span>
          {incomplete && (
            <span className="filter-incomplete-badge" title="Finish this filter for it to affect results">
              Incomplete &mdash; not applied
            </span>
          )}
        </div>

        <div className="filter-row-actions">
          <button
            type="button"
            className="filter-move-btn"
            aria-label={`Move filter ${index + 1} up`}
            disabled={index === 0}
            onClick={onMoveUp}
          >
            <i className="bi bi-arrow-up"></i>
          </button>
          <button
            type="button"
            className="filter-move-btn"
            aria-label={`Move filter ${index + 1} down`}
            disabled={index === total - 1}
            onClick={onMoveDown}
          >
            <i className="bi bi-arrow-down"></i>
          </button>
          <button
            type="button"
            className="remove-filter-btn"
            aria-label={`Remove filter ${index + 1}`}
            onClick={onRemove}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      </div>

      <div className="filter-controls">{children}</div>
    </div>
  );
}

const CustomFilters = ({
  columns = [],
  filters = [],
  onChange = () => {},
  availableFieldOptions = {}
}) => {
  const [customFilters, setCustomFilters] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    setCustomFilters(normalizeFilterList(filters));
  }, [filters]);

  const commit = (updatedFilters) => {
    const normalized = normalizeFilterList(updatedFilters);
    setCustomFilters(normalized);
    onChange(normalized);
  };

  const addFilter = () => {
    commit([
      ...customFilters,
      {
        id: nextFilterId(),
        field: '',
        operator: 'equals',
        value: '',
        conjunction: 'AND'
      }
    ]);
  };

  const removeFilter = (filterId) => {
    commit(customFilters.filter(f => f.id !== filterId));
  };

  const updateFilter = (filterId, field, newValue) => {
    commit(customFilters.map(filter => {
      if (filter.id !== filterId) return filter;

      const updated = { ...filter, [field]: newValue };

      // Clear value when changing field or operator to avoid invalid combinations
      if (field === 'field' || field === 'operator') {
        updated.value = '';
      }

      return updated;
    }));
  };

  const setConjunction = (index, conjunction) => {
    commit(customFilters.map((filter, i) => (
      i === index ? { ...filter, conjunction } : filter
    )));
  };

  /**
   * Move a row through the expression. The AND/OR joins keep their positions on
   * screen so the shape of the expression stays put while the rows slide through
   * it -- only the row order changes.
   */
  const moveFilter = (from, to) => {
    if (from === to || to < 0 || to >= customFilters.length) return;

    const conjunctions = customFilters.map(f => normalizeFilterConjunction(f.conjunction));
    commit(arrayMove(customFilters, from, to).map((filter, index) => ({
      ...filter,
      conjunction: index === 0 ? 'AND' : conjunctions[index]
    })));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const from = customFilters.findIndex(f => f.id === active.id);
    const to = customFilters.findIndex(f => f.id === over.id);
    if (from === -1 || to === -1) return;

    moveFilter(from, to);
  };

  const getFieldOptions = () => columns.map(col => ({ value: col.key, label: col.label }));

  const getSelectedValues = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) return value.map(v => ({ value: v, label: v }));
    return { value: value, label: value };
  };

  const getValueOptions = (fieldKey) => availableFieldOptions[fieldKey] || [];

  const getOperatorOptions = (fieldKey) => {
    const column = columns.find(col => col.key === fieldKey);
    if (!column) return OPERATORS;

    // Customize available operators based on field type
    if (fieldKey === 'created_on' || fieldKey === 'updated_at') {
      return OPERATORS.filter(op =>
        ['equals', 'not_equals', 'greater_than', 'less_than', 'has_value', 'does_not_have_value'].includes(op.value)
      );
    }

    if (fieldKey === 'id' || fieldKey === 'task_count') {
      return OPERATORS.filter(op =>
        ['equals', 'not_equals', 'greater_than', 'less_than', 'has_value', 'does_not_have_value'].includes(op.value)
      );
    }

    // Default: all operators available
    return OPERATORS;
  };

  const isDateField = (fieldKey) =>
    fieldKey && (fieldKey.includes('_on') || fieldKey.includes('_at') || fieldKey.includes('date'));

  // AND binds tighter than OR, so the rows read as an OR of AND groups.
  const groups = useMemo(() => groupFilterIndexes(customFilters), [customFilters]);

  const describeFilter = (filter) => {
    const fieldLabel = columns.find(col => col.key === filter.field)?.label || filter.field;
    const operatorLabel = (
      OPERATORS.find(op => op.value === filter.operator)?.label || filter.operator
    ).toLowerCase();

    if (!shouldShowValueInput(filter.operator)) {
      return `${fieldLabel} ${operatorLabel}`;
    }

    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    return `${fieldLabel} ${operatorLabel} ${values.map(v => `"${v}"`).join(' or ')}`;
  };

  // Preview of what actually gets evaluated: incomplete rows are left out.
  const expressionSummary = useMemo(() => {
    const active = customFilters.filter(isFilterComplete);
    if (active.length === 0) return '';

    const activeGroups = groupFilterIndexes(active);
    return activeGroups
      .map((group) => {
        const text = group.map(index => describeFilter(active[index])).join(' AND ');
        return activeGroups.length > 1 && group.length > 1 ? `(${text})` : text;
      })
      .join(' OR ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFilters, columns]);

  const renderFilterControls = (filter) => (
    <>
      {/* Field Selection */}
      <div className="filter-control">
        <label>Field</label>
        <Select
          value={filter.field ? { value: filter.field, label: columns.find(col => col.key === filter.field)?.label || filter.field } : null}
          onChange={(option) => updateFilter(filter.id, 'field', option ? option.value : '')}
          options={getFieldOptions()}
          placeholder="Select field..."
          isClearable
          className="react-select-container"
          classNamePrefix="react-select"
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        />
      </div>

      {/* Operator Selection */}
      <div className="filter-control">
        <label>Operator</label>
        <Select
          value={filter.operator ? { value: filter.operator, label: OPERATORS.find(op => op.value === filter.operator)?.label || filter.operator } : null}
          onChange={(option) => updateFilter(filter.id, 'operator', option ? option.value : 'equals')}
          options={getOperatorOptions(filter.field)}
          placeholder="Select operator..."
          isDisabled={!filter.field}
          className="react-select-container"
          classNamePrefix="react-select"
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        />
      </div>

      {/* Value Input */}
      {shouldShowValueInput(filter.operator) && (
        <div className="filter-control">
          <label>Value{getValueOptions(filter.field).length > 0 ? ' (multi-select supported)' : ''}</label>
          {getValueOptions(filter.field).length > 0 ? (
            <Select
              value={getSelectedValues(filter.value)}
              onChange={(selectedOptions) => {
                const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                updateFilter(filter.id, 'value', values.length === 1 ? values[0] : values);
              }}
              options={getValueOptions(filter.field)}
              placeholder="Select value(s)..."
              isClearable
              isMulti
              isDisabled={!filter.field || !filter.operator}
              className="react-select-container"
              classNamePrefix="react-select"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          ) : isDateField(filter.field) ? (
            <input
              type="date"
              value={Array.isArray(filter.value) ? filter.value[0] || '' : (filter.value || '')}
              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
              min={DATE_INPUT_MIN}
              max={DATE_INPUT_MAX}
              {...datePickerTriggers}
              disabled={!filter.field || !filter.operator}
              className="filter-value-input"
            />
          ) : (
            <input
              type="text"
              value={Array.isArray(filter.value) ? filter.value.join(', ') : (filter.value || '')}
              onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
              placeholder="Enter value..."
              disabled={!filter.field || !filter.operator}
              className="filter-value-input"
            />
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="custom-filters-container">
      <div className="custom-filters-header">
        <h4>Custom Filters</h4>
        <button
          type="button"
          className="add-filter-btn"
          onClick={addFilter}
        >
          <i className="bi bi-plus"></i>
          Add Filter
        </button>
      </div>

      {customFilters.length === 0 && (
        <div className="no-filters-message">
          No custom filters added. Click "Add Filter" to create one.
        </div>
      )}

      {expressionSummary && (
        <div className="filter-summary">
          <span className="filter-summary-label">Matches when</span>
          <code className="filter-summary-expression">{expressionSummary}</code>
          <span className="filter-summary-hint">
            AND is evaluated before OR, so rows joined by AND form a group.
            Drag rows or use the arrows to change the grouping.
          </span>
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={customFilters.map(f => f.id)}
          strategy={verticalListSortingStrategy}
        >
          {groups.map((group, groupIndex) => (
            <React.Fragment key={`group-${customFilters[group[0]].id}`}>
              {groupIndex > 0 && (
                <ConjunctionToggle
                  value={customFilters[group[0]].conjunction}
                  onChange={(value) => setConjunction(group[0], value)}
                />
              )}

              <div className={`filter-group ${group.length > 1 ? 'is-grouped' : ''}`}>
                {group.length > 1 && (
                  <div className="filter-group-label">
                    <i className="bi bi-braces"></i>
                    Group of {group.length} &mdash; all must match
                  </div>
                )}

                {group.map((filterIndex, positionInGroup) => {
                  const filter = customFilters[filterIndex];

                  return (
                    <React.Fragment key={filter.id}>
                      {positionInGroup > 0 && (
                        <ConjunctionToggle
                          value={filter.conjunction}
                          onChange={(value) => setConjunction(filterIndex, value)}
                        />
                      )}

                      <SortableFilterRow
                        id={filter.id}
                        index={filterIndex}
                        total={customFilters.length}
                        incomplete={!isFilterComplete(filter)}
                        onRemove={() => removeFilter(filter.id)}
                        onMoveUp={() => moveFilter(filterIndex, filterIndex - 1)}
                        onMoveDown={() => moveFilter(filterIndex, filterIndex + 1)}
                      >
                        {renderFilterControls(filter)}
                      </SortableFilterRow>
                    </React.Fragment>
                  );
                })}
              </div>
            </React.Fragment>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default CustomFilters;
